import { useContext, type FunctionComponent } from 'react';
import { type OnChangeValue } from 'react-select';
import { getChoicesAnswer, getChosenOptionAnswer, getValueAnswer } from '../../../domain/answer';
import { buildChoicesAnswer } from '../../../domain/answer/buildChoicesAnswer';
import { buildChosenOptionAnswer } from '../../../domain/answer/buildChosenOptionAnswer';
import { buildValueAnswer } from '../../../domain/answer/buildValueAnswer';
import { upsert } from '../../../domain/answer/upsert';
import GenericInfoDTO from '../../../interfaces/dto/genericInfoDTO';
import Question from '../../../interfaces/question';
import { QuestionType } from '../../../interfaces/questionType';
import {
  ChoicesAnswer,
  ChosenOptionAnswer,
  MappedAnswer,
  ObjectId,
  SurveyPageContext,
  ValueAnswer,
} from '../../context/SurveyPageContext';
import ChooseMany from '../ChooseMany/ChooseMany';
import { DateInput } from '../Date/Date';
import Dropdown, { DropdownOption } from '../Dropdown/Dropdown';
import FillIn from '../FillIn/FillIn';
import Multiple from '../Multiple/Multiple';
import MultipleDynamic from '../MultipleDynamic/MultipleDynamic';
import RadioButton from '../RadioButton/RadioButton';
import RadioColor from '../RadioColor';
import RadioHtml from '../RadioHtml/RadioHtml';
import Textarea from '../Textarea/Textarea';
import { questionToDropdownOptions } from './dropdownMapper';
import UnderskinQuestion from './UnderskinQuestion';
import type { UnderskinQuestionType } from './UnderskinQuestion';

interface QuestionFactoryProps {
  question: Question;
}

const QuestionFactory: FunctionComponent<QuestionFactoryProps> = ({ question }) => {
  const {
    upsertMappedAnswers,
    mappedAnswers,
    setConditionalQuestionId,
    deleteConditionalQuestionId,
    conditionalQuestionIds,
  } = useContext(SurveyPageContext);

  const updateAnswer = (answer: MappedAnswer) => {
    const upsertedAnswers: Map<number, MappedAnswer> = upsert({
      answer,
      mappedAnswers,
    });
    upsertMappedAnswers(upsertedAnswers);
  };

  switch (question.type) {
    case QuestionType.RADIO_BUTTON: {
      const questionId = question.id;
      const radioButtonAnswer = getChosenOptionAnswer(mappedAnswers, questionId);
      const selectedOption = question.radioButtonOptions.find(
        (o) => o.id === radioButtonAnswer?.chosenOption?.id,
      );
      if (
        selectedOption &&
        selectedOption.conditionalQuestionId &&
        !conditionalQuestionIds.has(questionId)
      ) {
        setConditionalQuestionId(questionId, [selectedOption.conditionalQuestionId]);
      } else if (
        selectedOption &&
        !selectedOption.conditionalQuestionId &&
        conditionalQuestionIds.has(questionId)
      ) {
        deleteConditionalQuestionId(questionId);
      }

      const onChange = ({
        newOption,
        isOtherOption,
      }: {
        newOption: GenericInfoDTO;
        isOtherOption: boolean;
      }) => {
        if (isOtherOption) {
          const answer: ValueAnswer = buildValueAnswer({
            questionId,
            value: '',
          });
          updateAnswer(answer);
        } else {
          const { id: optionId, conditionalQuestionId } = newOption;
          const answer: ChosenOptionAnswer = buildChosenOptionAnswer({
            questionId,
            optionId,
          });
          conditionalQuestionId
            ? setConditionalQuestionId(questionId, [conditionalQuestionId])
            : deleteConditionalQuestionId(questionId);
          updateAnswer(answer);
        }
      };
      const onChangeInputText = ({ newContent }: { newContent: string }) => {
        const answer: ValueAnswer = buildValueAnswer({
          questionId,
          value: newContent,
        });
        updateAnswer(answer);
      };
      const additionalText = () => {
        const answer = mappedAnswers.get(questionId);
        if (
          answer &&
          !(answer as ChosenOptionAnswer).chosenOption &&
          (answer as ValueAnswer).value !== null
        ) {
          return (answer as ValueAnswer).value;
        }
        return null;
      };
      return (
        <RadioButton
          key={`radioButton-${questionId}`}
          question={question}
          onChange={onChange}
          radioButtonAnswer={radioButtonAnswer}
          additionalText={additionalText()}
          onChangeInputText={onChangeInputText}
        />
      );
    }
    case QuestionType.RADIO_COLOR: {
      const radioButtonAnswer = getChosenOptionAnswer(mappedAnswers, question.id);
      const questionId = question.id;
      const onChange = (optionId: number) => {
        const answer: ChosenOptionAnswer = buildChosenOptionAnswer({
          questionId,
          optionId,
        });
        updateAnswer(answer);
      };
      return (
        <RadioColor question={question} onChange={onChange} radioButtonAnswer={radioButtonAnswer} />
      );
    }
    case QuestionType.RADIO_HTML: {
      const radioButtonAnswer = getChosenOptionAnswer(mappedAnswers, question.id);
      const questionId = question.id;
      const onChange = (optionId: number) => {
        const answer: ChosenOptionAnswer = buildChosenOptionAnswer({
          questionId,
          optionId,
        });
        updateAnswer(answer);
      };

      const htmlOption = (option: GenericInfoDTO) => {
        const type = option.explanation as UnderskinQuestionType;
        return <UnderskinQuestion type={type} description={option.description || ''} />;
      };

      return (
        <RadioHtml
          question={question}
          onChange={onChange}
          radioButtonAnswer={radioButtonAnswer}
          htmlOption={htmlOption}
        />
      );
    }
    case QuestionType.MULTIPLE:
      return <Multiple questions={question.questions} />;
    case QuestionType.MULTIPLE_DYNAMIC:
      return <MultipleDynamic questions={question.questions} />;
    case QuestionType.SELECT_SIMPLE: {
      const dropdownOptions = questionToDropdownOptions(
        question,
        getChosenOptionAnswer(mappedAnswers, question.id),
      );
      const onChange = (val: OnChangeValue<DropdownOption, false>) => {
        const questionId = question.id;
        const option = val as DropdownOption;
        const optionId = parseInt(option.value, 10);
        const answer: ChosenOptionAnswer = buildChosenOptionAnswer({
          questionId,
          optionId,
        });
        updateAnswer(answer);
      };
      const placeholder = '- Select an option -';
      return (
        <Dropdown
          key={`question-${question.id}`}
          values={dropdownOptions}
          onChange={onChange}
          placeholder={placeholder}
        />
      );
    }
    case QuestionType.DATE_IN: {
      const answer: ValueAnswer = getValueAnswer(mappedAnswers, question.id);
      const date = answer.value ? new Date(answer.value) : undefined;
      return <DateInput question={question} value={date} />;
    }
    case QuestionType.TEXTAREA_IN: {
      const textAreaContent: ValueAnswer = getValueAnswer(mappedAnswers, question.id);
      const onChange = (value: string) => {
        const questionId = question.id;
        const answer: ValueAnswer = {
          question: { id: questionId },
          value,
        };
        updateAnswer(answer);
      };
      return (
        <Textarea
          content={textAreaContent.value}
          onChange={onChange}
          key={`question-${question.id}`}
        />
      );
    }
    case QuestionType.FILL_IN: {
      const fillInContent: ValueAnswer = getValueAnswer(mappedAnswers, question.id);
      const onChange = (value: string) => {
        const questionId = question.id;
        const answer: ValueAnswer = buildValueAnswer({ questionId, value });
        updateAnswer(answer);
      };
      return <FillIn content={fillInContent.value} onChange={onChange} />;
    }
    case QuestionType.CHOOSE_MANY: {
      const answer = getChoicesAnswer(mappedAnswers, question.id);
      const alreadyOnAnswer = (id: number) => !!answer?.choices.find((c) => c.id === id);
      const questionId = question.id;
      const onChange = (choiceId: number, newValue: boolean) => {
        const existentValue = alreadyOnAnswer(choiceId);
        const choiceObject = question.choices.find(
          (c) => c.conditionalQuestionId && c.id === choiceId,
        );

        const tryToAddConditionalQuestionId = () => {
          if (choiceObject?.conditionalQuestionId) {
            conditionalQuestionIds.has(questionId)
              ? setConditionalQuestionId(
                  questionId,
                  Array.from(
                    new Set([
                      ...(conditionalQuestionIds.get(questionId) as number[]),
                      choiceObject.conditionalQuestionId,
                    ]).values(),
                  ),
                )
              : setConditionalQuestionId(questionId, [choiceObject.conditionalQuestionId]);
          }
        };

        const tryToRemoveConditionalQuestionId = () => {
          if (
            choiceObject?.conditionalQuestionId &&
            conditionalQuestionIds.has(questionId) &&
            conditionalQuestionIds.get(questionId)?.includes(choiceObject.conditionalQuestionId)
          ) {
            const ids = conditionalQuestionIds.get(questionId);
            setConditionalQuestionId(
              questionId,
              ids?.filter((id) => id !== choiceObject.conditionalQuestionId) as number[],
            );
          }
        };
        if (newValue && !existentValue) {
          const choices: ObjectId[] = [...(answer?.choices || []), { id: choiceId }];
          const newAnswer: ChoicesAnswer = buildChoicesAnswer({
            questionId,
            choices,
          });
          tryToAddConditionalQuestionId();
          updateAnswer(newAnswer);
        } else if (!newValue) {
          const choices: ObjectId[] = [...(answer?.choices.filter((c) => c.id !== choiceId) || [])];
          const newAnswer: ChoicesAnswer = buildChoicesAnswer({
            questionId,
            choices,
          });
          tryToRemoveConditionalQuestionId();
          updateAnswer(newAnswer);
        }
      };
      const choiceItems = question.choices.map((c) => ({
        id: c.id,
        label: c.value || '',
        checked: alreadyOnAnswer(c.id),
      }));
      const conditionalQuestions = question.choices.filter(
        (c) => c.conditionalQuestionId && alreadyOnAnswer(c.id),
      );
      if (conditionalQuestions.length > 0 && !conditionalQuestionIds.has(questionId)) {
        setConditionalQuestionId(
          questionId,
          conditionalQuestions.map((c) => c.conditionalQuestionId) as number[],
        );
      }
      return <ChooseMany choiceItems={choiceItems} onChange={onChange} />;
    }
    default:
      return <></>;
  }
};

export default QuestionFactory;
